import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../config';

/**
 * SmartAssessment - Identity-Anchored Assessment Wizard
 * 
 * Flow:
 * 1. Identity Hook: "I want to be the person who..."
 * 2. Habit Bridge: Small action to prove identity (AI validates)
 * 3. Chaos Check: Warn if active goals exist
 * 4. Confidence Slider: 1-10 confidence rating
 * 5. Duration Selector: 14/21/30 days
 * 6. Tiny Win & Anchor: Make it tiny and anchor it
 */

const SmartAssessment = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [activeGoals, setActiveGoals] = useState([]);
  const [user, setUser] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  
  // Assessment Data
  const [identityStatement, setIdentityStatement] = useState('');
  const [habitAction, setHabitAction] = useState('');
  const [confidence, setConfidence] = useState(7);
  const [duration, setDuration] = useState(14);
  const [chaosDecision, setChaosDecision] = useState(null); // 'pause' | 'proceed' | null
  
  // New: Tiny Win & Anchor (Step 6)
  const [tinyWin, setTinyWin] = useState('');
  const [anchorHabit, setAnchorHabit] = useState('');

  // Check for active goals on load
  useEffect(() => {
    const checkActiveGoals = async () => {
      try {
        const [userRes, allJourneysRes] = await Promise.all([
          axios.get(`${API}/me`),
          axios.get(`${API}/journey/all`)
        ]);
        setUser(userRes.data);
        
        // Get ALL active journeys for multi-goal check
        const activeJourneys = allJourneysRes.data.journeys || [];
        setActiveGoals(activeJourneys);
        
        console.log('Active goals found:', activeJourneys.length);
      } catch (err) {
        console.error('Failed to check active goals:', err);
        // Fallback: try single journey endpoint
        try {
          const journeyRes = await axios.get(`${API}/journey`);
          if (journeyRes.data.has_active_journey) {
            setActiveGoals([journeyRes.data.journey]);
          }
        } catch (e) {
          console.error('Fallback also failed:', e);
        }
      } finally {
        setLoading(false);
      }
    };
    checkActiveGoals();
  }, []);

  // AI suggestion when confidence is low (derived during render)
  const aiSuggestion = (confidence < 7 && habitAction)
    ? {
        type: 'low_confidence',
        message: `Your confidence is ${confidence}/10. Consider either:`,
        options: [
          { label: 'Make the habit smaller (Tiny Win)', action: 'shrink' },
          { label: 'Start with 7 days instead', action: 'shorten' }
        ]
      }
    : null;

  const canProceedFromStep = (currentStep) => {
    switch (currentStep) {
      case 1: return identityStatement.trim().length >= 10;
      case 2: return habitAction.trim().length >= 5;
      case 3: 
        // Block if at 3 goals (hard limit)
        if (activeGoals.length >= 3) return false;
        // Otherwise need a decision
        return !activeGoals.length || chaosDecision !== null;
      case 4: return confidence >= 1 && confidence <= 10;
      case 5: return [7, 14, 21, 30].includes(duration);
      case 6: return tinyWin.trim().length >= 3 && anchorHabit.trim().length >= 3;
      default: return false;
    }
  };

  const handleNext = () => {
    // Skip chaos check if no active goals
    if (step === 2 && activeGoals.length === 0) {
      setStep(4);
    } else if (step < 6) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    // Skip chaos check going back if no active goals
    if (step === 4 && activeGoals.length === 0) {
      setStep(2);
    } else if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      // Create the journey directly with all the data we've collected
      const journeyData = {
        identity_goal: identityStatement,
        target_habit: habitAction,
        tiny_win: tinyWin,
        anchor_habit: anchorHabit,
        duration: duration,
        confidence: confidence
      };
      
      await axios.post(`${API}/journey`, journeyData);
      
      // Save smart assessment data to backend (optional tracking)
      try {
        await axios.post(`${API}/assessment/smart`, {
          identity_statement: identityStatement,
          habit_action: habitAction,
          confidence: confidence,
          duration: duration,
          chaos_decision: chaosDecision,
          had_active_goals: activeGoals.length > 0,
          tiny_win: tinyWin,
          anchor_habit: anchorHabit
        });
      } catch (e) {
        // Assessment save is optional, don't fail if it errors
        console.log('Assessment tracking failed (non-critical):', e);
      }
      
      // Show success modal with reminder tip
      setShowSuccessModal(true);
    } catch (err) {
      console.error('Failed to create journey:', err);
      alert('Failed to create your journey. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePauseExisting = async () => {
    try {
      await axios.post(`${API}/journey/pause`);
      setChaosDecision('pause');
    } catch (err) {
      console.error('Failed to pause journey:', err);
      setChaosDecision('pause'); // Still proceed
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-500 border-t-transparent"></div>
      </div>
    );
  }

  // Calculate total steps and display step
  // If no active goals, skip step 3 (chaos check) so total is 5, otherwise 6
  const hasActiveGoals = activeGoals.length > 0;
  const totalSteps = hasActiveGoals ? 6 : 5;
  
  // Adjust display step when chaos check is skipped
  const getDisplayStep = () => {
    if (!hasActiveGoals && step > 2) {
      return step - 1; // Skip step 3 in display
    }
    return step;
  };
  const displayStep = getDisplayStep();
  const progress = (displayStep / totalSteps) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex flex-col">
      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-5xl">🎉</span>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">You&apos;re All Set!</h2>
            <p className="text-gray-600 mb-6">Your journey has been created. Time to build that habit!</p>
            
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 text-left">
              <div className="flex items-start gap-3">
                <span className="text-2xl">⏰</span>
                <div>
                  <p className="font-semibold text-amber-800">Set Your Reminder Time</p>
                  <p className="text-sm text-amber-700 mt-1">
                    Head to <strong>Settings</strong> to customize when you receive your daily nudges and reminders.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="space-y-3">
              <button
                onClick={() => navigate('/settings')}
                className="w-full bg-amber-500 hover:bg-amber-600 text-white font-semibold py-3 rounded-lg transition"
              >
                ⚙️ Go to Settings
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 rounded-lg transition"
              >
                🏠 Go to Dashboard
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white shadow-sm px-4 py-4">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-xl font-bold text-gray-900">🎯 Define Your Journey</h1>
          <p className="text-sm text-gray-500 mt-1">Step {displayStep} of {totalSteps}</p>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-gray-200 h-2">
        <div 
          className="bg-emerald-500 h-full transition-all duration-500"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-lg">
          
          {/* Step 1: Identity Hook */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <span className="text-4xl">🌟</span>
                <h2 className="text-2xl font-bold text-gray-900 mt-3">Who Do You Want to Become?</h2>
                <p className="text-gray-500 mt-2">Habits stick when tied to identity, not just tasks.</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Complete this sentence:
                </label>
                <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-2">
                  <span className="text-gray-500 sm:mt-3 whitespace-nowrap">&quot;I want to be the person who</span>
                  <div className="flex items-end gap-1 flex-1 min-w-0">
                    <input
                      type="text"
                      value={identityStatement}
                      onChange={(e) => setIdentityStatement(e.target.value)}
                      placeholder="exercises every day..."
                      className="flex-1 min-w-0 border-b-2 border-emerald-300 focus:border-emerald-500 outline-none py-2 text-lg"
                      autoFocus
                    />
                    <span className="text-gray-500 pb-2">&quot;</span>
                  </div>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <p className="text-amber-800 text-sm">
                  <strong>💡 Examples:</strong><br/>
                  • &quot;...reads every day&quot;<br/>
                  • &quot;...takes care of their health&quot;<br/>
                  • &quot;...shows up consistently&quot;
                </p>
              </div>
            </div>
          )}

          {/* Step 2: Habit Bridge */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <span className="text-4xl">🌉</span>
                <h2 className="text-2xl font-bold text-gray-900 mt-3">Prove It to Yourself</h2>
                <p className="text-gray-500 mt-2">What&apos;s ONE small action that proves you&apos;re becoming this person?</p>
              </div>

              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-4">
                <p className="text-emerald-800 text-sm">
                  <strong>Your Identity:</strong> &quot;I want to be the person who {identityStatement}&quot;
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  To prove this to yourself, what is ONE small thing you could do daily?
                </label>
                <textarea
                  value={habitAction}
                  onChange={(e) => setHabitAction(e.target.value)}
                  placeholder="e.g., Do 5 pushups, Read for 10 minutes, Meditate for 2 minutes..."
                  className="w-full border-2 border-gray-200 focus:border-emerald-500 rounded-xl p-4 outline-none resize-none h-24"
                  autoFocus
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <p className="text-blue-800 text-sm">
                  <strong>🎯 Tiny Win Tip:</strong> Think about what you could do even on your WORST day - tired, busy, stressed. That&apos;s your baseline.
                </p>
              </div>
            </div>
          )}

          {/* Step 3: Chaos Check - Different scenarios based on goal count */}
          {step === 3 && activeGoals.length > 0 && (
            <div className="space-y-6">
              {/* Scenario: Already at 3 goals - HARD LIMIT */}
              {activeGoals.length >= 3 ? (
                <>
                  <div className="text-center mb-6">
                    <span className="text-4xl">🛑</span>
                    <h2 className="text-2xl font-bold text-red-600 mt-3">Maximum Goals Reached</h2>
                    <p className="text-gray-500 mt-2">You already have 3 active goals - that&apos;s the limit!</p>
                  </div>

                  <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                    <p className="text-red-800 font-medium">Your Active Goals:</p>
                    <ul className="mt-2 space-y-2">
                      {activeGoals.map((goal, idx) => (
                        <li key={idx} className="text-red-700 text-sm flex items-center gap-2">
                          <span>🎯</span>
                          <span>{goal.tiny_win || goal.target_habit || `Goal ${idx + 1}`}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <p className="text-amber-800 text-sm">
                      <strong>💡 Recommendation:</strong> Complete or archive one of your existing goals before starting a new one. Quality over quantity leads to lasting change.
                    </p>
                  </div>

                  <button
                    onClick={() => navigate('/dashboard')}
                    className="w-full p-4 rounded-xl bg-gray-100 hover:bg-gray-200 transition text-center"
                  >
                    <div className="font-semibold text-gray-900">← Return to Dashboard</div>
                    <div className="text-sm text-gray-500">Manage your existing goals</div>
                  </button>
                </>
              ) : activeGoals.length === 2 ? (
                /* Scenario: Has 2 goals - Warning about adding 3rd */
                <>
                  <div className="text-center mb-6">
                    <span className="text-4xl">⚠️</span>
                    <h2 className="text-2xl font-bold text-orange-600 mt-3">Adding Goal 3 of 3</h2>
                    <p className="text-gray-500 mt-2">This will be your final goal slot.</p>
                  </div>

                  <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                    <p className="text-orange-800 font-medium">Your Current Goals ({activeGoals.length}/3):</p>
                    <ul className="mt-2 space-y-2">
                      {activeGoals.map((goal, idx) => (
                        <li key={idx} className="text-orange-700 text-sm flex items-center gap-2">
                          <span>🎯</span>
                          <span>{goal.tiny_win || goal.target_habit || `Goal ${idx + 1}`}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <p className="text-amber-800 text-sm">
                      <strong>📊 Research shows:</strong> Managing 3 goals simultaneously requires significant mental bandwidth. Make sure each goal has a clear tiny win.
                    </p>
                  </div>

                  <p className="text-gray-600 text-center">What would you like to do?</p>

                  <div className="space-y-3">
                    <button
                      onClick={() => setChaosDecision('proceed')}
                      className={`w-full p-4 rounded-xl border-2 transition text-left ${
                        chaosDecision === 'proceed' 
                          ? 'border-emerald-500 bg-emerald-50' 
                          : 'border-gray-200 hover:border-emerald-300'
                      }`}
                    >
                      <div className="font-semibold text-gray-900">✅ Add 3rd Goal</div>
                      <div className="text-sm text-gray-500">I&apos;m ready to manage all three</div>
                    </button>

                    <button
                      onClick={() => navigate('/dashboard')}
                      className="w-full p-4 rounded-xl border-2 border-gray-200 hover:border-gray-300 transition text-left"
                    >
                      <div className="font-semibold text-gray-900">← Go Back</div>
                      <div className="text-sm text-gray-500">Focus on existing goals first</div>
                    </button>
                  </div>
                </>
              ) : (
                /* Scenario: Has 1 goal - Standard chaos check */
                <>
                  <div className="text-center mb-6">
                    <span className="text-4xl">⚠️</span>
                    <h2 className="text-2xl font-bold text-gray-900 mt-3">Focus Check</h2>
                    <p className="text-gray-500 mt-2">You have {activeGoals.length} active goal{activeGoals.length > 1 ? 's' : ''} in progress.</p>
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <p className="text-amber-800 font-medium">Current Goal:</p>
                    <p className="text-amber-700 mt-1">{activeGoals[0]?.tiny_win || activeGoals[0]?.target_habit || 'Active habit'}</p>
                    <p className="text-amber-600 text-sm mt-2">
                      Day {activeGoals[0]?.day_count || activeGoals[0]?.current_day || '?'} of {activeGoals[0]?.duration || 14}
                    </p>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <p className="text-blue-800 text-sm">
                      <strong>📊 Science says:</strong> Focusing on one habit at a time increases success by 80%. Multiple goals spread your willpower thin.
                    </p>
                  </div>

                  <p className="text-gray-600 text-center">What would you like to do?</p>

                  <div className="space-y-3">
                    <button
                      onClick={handlePauseExisting}
                      className={`w-full p-4 rounded-xl border-2 transition text-left ${
                        chaosDecision === 'pause' 
                          ? 'border-emerald-500 bg-emerald-50' 
                          : 'border-gray-200 hover:border-emerald-300'
                      }`}
                    >
                      <div className="font-semibold text-gray-900">🎯 Pause Existing Goal</div>
                      <div className="text-sm text-gray-500">Focus fully on your new identity</div>
                    </button>

                    <button
                      onClick={() => setChaosDecision('proceed')}
                      className={`w-full p-4 rounded-xl border-2 transition text-left ${
                        chaosDecision === 'proceed' 
                          ? 'border-orange-500 bg-orange-50' 
                          : 'border-gray-200 hover:border-orange-300'
                      }`}
                    >
                      <div className="font-semibold text-gray-900">💪 Add Another Goal</div>
                      <div className="text-sm text-gray-500">I can handle multiple goals (2 of 3)</div>
                    </button>
                  </div>

                  {chaosDecision === 'proceed' && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
                      <p className="text-emerald-800 text-sm">
                        <strong>Great!</strong> You&apos;ll have 2 active goals. Remember: it&apos;s okay to pause one if it gets overwhelming.
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Step 4: Confidence Slider */}
          {step === 4 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <span className="text-4xl">📊</span>
                <h2 className="text-2xl font-bold text-gray-900 mt-3">Confidence Check</h2>
                <p className="text-gray-500 mt-2">Be honest with yourself.</p>
              </div>

              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-4">
                <p className="text-emerald-800 text-sm">
                  <strong>Your Habit:</strong> {habitAction}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-4">
                  How confident are you that you can do this EVERY day for the next 14 days?
                </label>
                
                <input
                  type="range"
                  min={1}
                  max={10}
                  value={confidence}
                  onChange={(e) => setConfidence(parseInt(e.target.value))}
                  className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-emerald-500"
                />
                
                <div className="flex justify-between mt-2">
                  <span className="text-sm text-gray-500">Not confident</span>
                  <span className={`text-3xl font-bold ${
                    confidence >= 7 ? 'text-emerald-600' : 
                    confidence >= 4 ? 'text-amber-600' : 'text-red-600'
                  }`}>
                    {confidence}
                  </span>
                  <span className="text-sm text-gray-500">Very confident</span>
                </div>
              </div>

              {aiSuggestion && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <p className="text-amber-800 font-medium mb-3">{aiSuggestion.message}</p>
                  <div className="space-y-2">
                    {aiSuggestion.options.map((opt, idx) => (
                      <button
                        key={idx}
                        onClick={() => {
                          if (opt.action === 'shrink') {
                            setStep(2); // Go back to habit
                          } else if (opt.action === 'shorten') {
                            setDuration(7);
                            setStep(5);
                          }
                        }}
                        className="w-full text-left p-3 bg-white rounded-lg border border-amber-300 hover:bg-amber-100 transition text-sm"
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 5: Duration Selector */}
          {step === 5 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <span className="text-4xl">📅</span>
                <h2 className="text-2xl font-bold text-gray-900 mt-3">Choose Your Journey Length</h2>
                <p className="text-gray-500 mt-2">Start small. You can always extend later.</p>
              </div>

              <div className="space-y-3">
                {[
                  { days: 7, label: 'Quick Start', desc: 'Perfect for building momentum', icon: '⚡' },
                  { days: 14, label: 'Starter', desc: 'Recommended for new habits', icon: '🌱', recommended: true },
                  { days: 21, label: 'Builder', desc: 'Solidify the habit', icon: '🔨' },
                  { days: 30, label: 'Master', desc: 'Full habit transformation', icon: '🏆' }
                ].map((option) => (
                  <button
                    key={option.days}
                    onClick={() => setDuration(option.days)}
                    className={`w-full p-4 rounded-xl border-2 transition text-left relative ${
                      duration === option.days 
                        ? 'border-emerald-500 bg-emerald-50' 
                        : 'border-gray-200 hover:border-emerald-300'
                    }`}
                  >
                    {option.recommended && (
                      <span className="absolute -top-2 -right-2 bg-emerald-500 text-white text-xs px-2 py-1 rounded-full">
                        Recommended
                      </span>
                    )}
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{option.icon}</span>
                      <div>
                        <div className="font-semibold text-gray-900">{option.days} Days - {option.label}</div>
                        <div className="text-sm text-gray-500">{option.desc}</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              {confidence < 7 && duration > 14 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                  <p className="text-amber-800 text-sm">
                    <strong>💡 Tip:</strong> With confidence at {confidence}/10, starting with 14 days or less might set you up for success better.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Step 6: Tiny Win & Anchor */}
          {step === 6 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <span className="text-4xl">🎯</span>
                <h2 className="text-2xl font-bold text-gray-900 mt-3">Make It Tiny & Anchor It</h2>
                <p className="text-gray-500 mt-2">The smallest possible version that still moves you forward.</p>
              </div>

              {/* Summary of identity and habit */}
              <div className="bg-emerald-50 rounded-xl p-4 mb-4">
                <p className="text-sm text-emerald-800">
                  <strong>Your Identity:</strong> &quot;I want to be the person who {identityStatement}&quot;
                </p>
                <p className="text-sm text-emerald-700 mt-1">
                  <strong>Your Habit:</strong> {habitAction}
                </p>
              </div>

              {/* Tiny Win Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  🌱 What&apos;s the TINY version of this habit? (2 minutes or less)
                </label>
                <input
                  type="text"
                  value={tinyWin}
                  onChange={(e) => setTinyWin(e.target.value)}
                  placeholder="e.g., Do 2 push-ups, Read 1 page, Write 1 sentence..."
                  className="w-full border-2 border-gray-200 focus:border-emerald-500 rounded-xl p-3 outline-none"
                  autoFocus
                />
                <p className="text-xs text-gray-500 mt-1">
                  Make it so small you can&apos;t say no, even on your worst day.
                </p>
              </div>

              {/* Anchor Habit Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  ⚓ What existing habit will you attach this to?
                </label>
                <input
                  type="text"
                  value={anchorHabit}
                  onChange={(e) => setAnchorHabit(e.target.value)}
                  placeholder="e.g., After I brush my teeth, After I pour my coffee..."
                  className="w-full border-2 border-gray-200 focus:border-emerald-500 rounded-xl p-3 outline-none"
                />
                <p className="text-xs text-gray-500 mt-1">
                  &quot;After I [existing habit], I will [tiny win]&quot;
                </p>
              </div>

              {/* Preview */}
              {tinyWin && anchorHabit && (
                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-4 border border-purple-200">
                  <p className="text-sm font-medium text-purple-900">✨ Your Commitment:</p>
                  <p className="text-purple-800 mt-1">
                    &quot;{anchorHabit}, I will {tinyWin}&quot;
                  </p>
                  <p className="text-xs text-purple-600 mt-2">
                    For {duration} days, to become someone who {identityStatement}
                  </p>
                </div>
              )}
            </div>
          )}

        </div>
      </div>

      {/* Navigation */}
      <div className="bg-white border-t p-4">
        <div className="max-w-lg mx-auto flex justify-between">
          <button
            onClick={handleBack}
            disabled={step === 1}
            className={`px-6 py-3 rounded-xl font-medium ${
              step === 1
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            ← Back
          </button>

          {step === 6 ? (
            <button
              onClick={handleSubmit}
              disabled={!canProceedFromStep(step) || submitting}
              className={`px-8 py-3 rounded-xl font-semibold ${
                canProceedFromStep(step) && !submitting
                  ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {submitting ? 'Creating Journey...' : '🚀 Start My Journey'}
            </button>
          ) : (
            <button
              onClick={handleNext}
              disabled={!canProceedFromStep(step)}
              className={`px-8 py-3 rounded-xl font-semibold ${
                canProceedFromStep(step)
                  ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              Next →
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SmartAssessment;
